/**
 * CapsuleIcon Component
 *
 * A note about efficiency:
 *
 * This component is a simple wrapper for inline svg elements used as re-usable
 * icons throughout the application.  Each svg loaded by this component must be
 * included in the js bundle, so it may not make sense to include svg's here
 * if they're only used once in some rarely-seen part of the application.  Extensibility
 * is pretty straightforward, just apply common sense where appropriate.
 */

import type { SVGProps } from 'react';

import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import InlineSVG from 'svg-inline-react';

import AddIcon from 'public/add-icon.svg';
import Bag from 'public/Bag.svg';
import BrowseCategoriesArrow from 'public/browse_categories_arrow.svg';
import Calendar from 'public/calendar.svg';
import Camera from 'public/camera.svg';
import CapsuleLogoWhite from 'public/capsulerx-logo-white.svg';
import CapsuleLogo from 'public/capsulerx-logo.svg';
import ChatIcon from 'public/chat-icon.svg';
import CheckCircleIcon from 'public/check-circle-icon.svg';
import CheckCircleLargeGreenIcon from 'public/check-circle-large-green.svg';
import CheckoutAccountIcon from 'public/Checkout_Icon_Account.svg';
import CheckoutAddressIcon from 'public/Checkout_Icon_Address.svg';
import CheckoutAddressEmptyIcon from 'public/Checkout_Icon_Address_Empty.svg';
import CheckoutDeliveryIcon from 'public/Checkout_Icon_Delivery.svg';
import CheckoutPaymentIcon from 'public/Checkout_Icon_Payment.svg';
import CheckoutPaymentEmptyIcon from 'public/Checkout_Icon_Payment_Empty.svg';
import ChevronLeft from 'public/chevron-left.svg';
import ChevronRight from 'public/chevron_right.svg';
import ClipboardIcon from 'public/clipboard.svg';
import ClockIcon from 'public/clock_icon.svg';
import CloseCircleIcon from 'public/close-circle-icon.svg';
import CloseIcon from 'public/close.svg';
import Coins from 'public/coins.svg';
import CopyIcon from 'public/copy-icon.svg';
import DeleteInputIcon from 'public/deleteInput.svg';
import DoctorSearchIcon from 'public/doctor.svg';
import EnvelopeSecure from 'public/envelope-secure.svg';
import FirstAidShield from 'public/first-aid-shield.svg';
import GoldStar from 'public/gold-star.svg';
import GreyStar from 'public/grey-star.svg';
import TransferringIcon from 'public/home_status_transferring_icon.svg';
import SystemChatIcon from 'public/icon-chat.svg';
import SystemInfoIcon from 'public/icon-info.svg';
import InsuranceIcon from 'public/Insurance_Icon.svg';
import Invisibility from 'public/invisibility.svg';
import LittlePill from 'public/little_pill.svg';
import LockSecure from 'public/lock_secure.svg';
import MailIcon from 'public/mail_icon.svg';
import DarkLocaleMarker from 'public/marker-dark.svg';
import WhiteLocaleMarker from 'public/marker.svg';
import MedicationSearchIcon from 'public/medication.svg';
import MinusIcon from 'public/minus.svg';
import SystemMoreIcon from 'public/more-icon.svg';
import SystemNavBackArrowIcon from 'public/nav-back-arrow.svg';
import SystemNavCloseIcon from 'public/nav-close.svg';
import SystemNavDownArrowIcon from 'public/nav-downward-arrow.svg';
import SystemNavForwardArrowIcon from 'public/nav-forward-arrow.svg';
import OldPharmacy from 'public/old_pharmacy.svg';
import OTCFilledLogo from 'public/otc_filled.svg';
import OTCOutlineLogo from 'public/otc_outline.svg';
import SystemPageOneIcon from 'public/page-1.svg';
import PaperIcon from 'public/Paper_Icon.svg';
import PaperclipIcon from 'public/paperclip.svg';
import Person from 'public/person.svg';
import PhoneIcon from 'public/Phone.svg';
import PhoneText from 'public/phone_text.svg';
import CheckoutPhotoIcon from 'public/Photo_Icon.svg';
import BottleIcon from 'public/pill-bottle-2.svg';
import PillBottleCircle from 'public/pill-bottle-circle.svg';
import PillBottleLarge from 'public/pill-bottle-large.svg';
import CheckoutAllergy from 'public/pill-bottle.svg';
import PillCircle from 'public/pill-circle.svg';
import PillDiamond from 'public/pill-diamond.svg';
import PillHeartIcon from 'public/pill-heart-animated.svg';
import PillSectionIcon from 'public/pill-section.svg';
import PillTall from 'public/pill-tall2.svg';
import PillTriangle from 'public/pill-triangle.svg';
import Play from 'public/Play.svg';
import SystemRadioButton from 'public/radio-button.svg';
import RemoveIcon from 'public/remove.svg';
import Reset from 'public/Reset.svg';
import RXBottle from 'public/Rx_Bottle.svg';
import RXFilledLogo from 'public/rx_filled.svg';
import RXOutlineLogo from 'public/rx_outline.svg';
import SystemSearchIcon from 'public/search-icon.svg';
import UpArrow from 'public/send.svg';
import StethoscopeIcon from 'public/stethoscope_icon.svg';
import StethoscopeIconGrey from 'public/stethoscope_icon_grey.svg';
import TrackerNeedsAction from 'public/TrackerNeedsAction.svg';
import TrackerProcessing from 'public/TrackerProcessing.svg';
import VideoControlPause from 'public/video_control_pause.svg';
import VideoControlPlay from 'public/video_control_play.svg';
import VideoControlReplay from 'public/video_control_replay.svg';
import Visibility from 'public/visibility.svg';
import YellowTriangle from 'public/yellow-triangle.svg';
import { CAPSULE_ICON_STRINGS } from 'src/consts';
import { CAPSULE_ICON } from 'src/consts/testIDs';

import styles from './styles.module.scss';

/**
 * Defines the iconString to iconSrc relationship
 */
const ICON_STRING_TO_SRC_MAP = {
  [CAPSULE_ICON_STRINGS.rx_bottle]: RXBottle,
  [CAPSULE_ICON_STRINGS.rx_filled]: RXFilledLogo,
  [CAPSULE_ICON_STRINGS.rx_outline]: RXOutlineLogo,
  [CAPSULE_ICON_STRINGS.otc_filled]: OTCFilledLogo,
  [CAPSULE_ICON_STRINGS.otc_outline]: OTCOutlineLogo,
  [CAPSULE_ICON_STRINGS.bag]: Bag,
  [CAPSULE_ICON_STRINGS.capsule_logo]: CapsuleLogo,
  [CAPSULE_ICON_STRINGS.capsule_logo_white]: CapsuleLogoWhite,
  [CAPSULE_ICON_STRINGS.checkout_account]: CheckoutAccountIcon,
  [CAPSULE_ICON_STRINGS.checkout_address]: CheckoutAddressIcon,
  [CAPSULE_ICON_STRINGS.checkout_address_empty]: CheckoutAddressEmptyIcon,
  [CAPSULE_ICON_STRINGS.checkout_allergy]: CheckoutAllergy,
  [CAPSULE_ICON_STRINGS.checkout_delivery]: CheckoutDeliveryIcon,
  [CAPSULE_ICON_STRINGS.checkout_payment]: CheckoutPaymentIcon,
  [CAPSULE_ICON_STRINGS.checkout_payment_empty]: CheckoutPaymentEmptyIcon,
  [CAPSULE_ICON_STRINGS.checkout_photo]: CheckoutPhotoIcon,
  [CAPSULE_ICON_STRINGS.check_circle]: CheckCircleIcon,
  [CAPSULE_ICON_STRINGS.check_circle_large_green]: CheckCircleLargeGreenIcon,
  [CAPSULE_ICON_STRINGS.chevron_left]: ChevronLeft,
  [CAPSULE_ICON_STRINGS.chevron_right]: ChevronRight,
  [CAPSULE_ICON_STRINGS.close_circle]: CloseCircleIcon,
  [CAPSULE_ICON_STRINGS.clipboard]: ClipboardIcon,
  [CAPSULE_ICON_STRINGS.coins]: Coins,
  [CAPSULE_ICON_STRINGS.copy_icon]: CopyIcon,
  [CAPSULE_ICON_STRINGS.envelope_secure]: EnvelopeSecure,
  [CAPSULE_ICON_STRINGS.first_aid_shield]: FirstAidShield,
  [CAPSULE_ICON_STRINGS.gold_star]: GoldStar,
  [CAPSULE_ICON_STRINGS.grey_star]: GreyStar,
  [CAPSULE_ICON_STRINGS.insurance]: InsuranceIcon,
  [CAPSULE_ICON_STRINGS.little_pill]: LittlePill,
  [CAPSULE_ICON_STRINGS.lock_secure]: LockSecure,
  [CAPSULE_ICON_STRINGS.person]: Person,
  [CAPSULE_ICON_STRINGS.paper_icon]: PaperIcon,
  [CAPSULE_ICON_STRINGS.paperclip]: PaperclipIcon,
  [CAPSULE_ICON_STRINGS.pill_bottle_circle]: PillBottleCircle,
  [CAPSULE_ICON_STRINGS.pill_bottle_large]: PillBottleLarge,
  [CAPSULE_ICON_STRINGS.pill_circle]: PillCircle,
  [CAPSULE_ICON_STRINGS.pill_heart_icon]: PillHeartIcon,
  [CAPSULE_ICON_STRINGS.pill_section]: PillSectionIcon,
  [CAPSULE_ICON_STRINGS.pill_tall]: PillTall,
  [CAPSULE_ICON_STRINGS.pill_triangle]: PillTriangle,
  [CAPSULE_ICON_STRINGS.pill_diamond]: PillDiamond,
  [CAPSULE_ICON_STRINGS.play]: Play,
  [CAPSULE_ICON_STRINGS.remove]: RemoveIcon,
  [CAPSULE_ICON_STRINGS.reset]: Reset,
  [CAPSULE_ICON_STRINGS.stethoscope_icon]: StethoscopeIcon,
  [CAPSULE_ICON_STRINGS.stethoscope_icon_grey]: StethoscopeIconGrey,
  [CAPSULE_ICON_STRINGS.system_chat]: SystemChatIcon,
  [CAPSULE_ICON_STRINGS.system_info]: SystemInfoIcon,
  [CAPSULE_ICON_STRINGS.system_more]: SystemMoreIcon,
  [CAPSULE_ICON_STRINGS.system_nav_back_arrow]: SystemNavBackArrowIcon,
  [CAPSULE_ICON_STRINGS.system_nav_close]: SystemNavCloseIcon,
  [CAPSULE_ICON_STRINGS.system_nav_down_arrow]: SystemNavDownArrowIcon,
  [CAPSULE_ICON_STRINGS.system_nav_forward_arrow]: SystemNavForwardArrowIcon,
  [CAPSULE_ICON_STRINGS.system_page_1]: SystemPageOneIcon,
  [CAPSULE_ICON_STRINGS.system_radio_button]: SystemRadioButton,
  [CAPSULE_ICON_STRINGS.system_search]: SystemSearchIcon,
  [CAPSULE_ICON_STRINGS.transferring]: TransferringIcon,
  [CAPSULE_ICON_STRINGS.video_control_pause]: VideoControlPause,
  [CAPSULE_ICON_STRINGS.video_control_play]: VideoControlPlay,
  [CAPSULE_ICON_STRINGS.video_control_replay]: VideoControlReplay,
  [CAPSULE_ICON_STRINGS.yellow_triangle]: YellowTriangle,
  [CAPSULE_ICON_STRINGS.doctor]: DoctorSearchIcon,
  [CAPSULE_ICON_STRINGS.medication]: MedicationSearchIcon,
  [CAPSULE_ICON_STRINGS.minus]: MinusIcon,
  [CAPSULE_ICON_STRINGS.delete_input]: DeleteInputIcon,
  [CAPSULE_ICON_STRINGS.white_marker]: WhiteLocaleMarker,
  [CAPSULE_ICON_STRINGS.dark_marker]: DarkLocaleMarker,
  [CAPSULE_ICON_STRINGS.phone]: PhoneIcon,
  [CAPSULE_ICON_STRINGS.phone_text]: PhoneText,
  [CAPSULE_ICON_STRINGS.pill_bottle]: BottleIcon,
  [CAPSULE_ICON_STRINGS.close]: CloseIcon,
  [CAPSULE_ICON_STRINGS.clock]: ClockIcon,
  [CAPSULE_ICON_STRINGS.mail]: MailIcon,
  [CAPSULE_ICON_STRINGS.camera]: Camera,
  [CAPSULE_ICON_STRINGS.browse_categories_arrow]: BrowseCategoriesArrow,
  [CAPSULE_ICON_STRINGS.chat_icon]: ChatIcon,
  [CAPSULE_ICON_STRINGS.calendar]: Calendar,
  [CAPSULE_ICON_STRINGS.visibility]: Visibility,
  [CAPSULE_ICON_STRINGS.invisibility]: Invisibility,
  [CAPSULE_ICON_STRINGS.up_arrow]: UpArrow,
  [CAPSULE_ICON_STRINGS.old_pharmacy]: OldPharmacy,
  [CAPSULE_ICON_STRINGS.add_icon]: AddIcon,
  [CAPSULE_ICON_STRINGS.tracker_needs_action]: TrackerNeedsAction,
  [CAPSULE_ICON_STRINGS.tracker_processing]: TrackerProcessing,
};

const DEFAULT_ICON_CLASS = styles.capsuleIcon;
const ICON_TO_CLASS_MAP = {
  [CAPSULE_ICON_STRINGS.rx_filled]: styles.capsuleLogo,
  [CAPSULE_ICON_STRINGS.rx_outline]: styles.capsuleLogo,
  [CAPSULE_ICON_STRINGS.otc_filled]: styles.capsuleLogo,
  [CAPSULE_ICON_STRINGS.otc_outline]: styles.capsuleLogo,
  [CAPSULE_ICON_STRINGS.capsule_logo]: styles.capsuleLogo,
  [CAPSULE_ICON_STRINGS.transferring]: styles.transferringIcon,
  [CAPSULE_ICON_STRINGS.system_nav_forward_arrow]: styles.arrowIconForward,
  [CAPSULE_ICON_STRINGS.checkout_account]: styles.checkoutIcon,
  [CAPSULE_ICON_STRINGS.checkout_address]: styles.checkoutIcon,
  [CAPSULE_ICON_STRINGS.checkout_address_empty]: styles.checkoutPaymentEmptyIcon,
  [CAPSULE_ICON_STRINGS.checkout_payment]: styles.checkoutIcon,
  [CAPSULE_ICON_STRINGS.checkout_payment_empty]: styles.checkoutPaymentEmptyIcon,
  [CAPSULE_ICON_STRINGS.checkout_delivery]: styles.checkoutIcon,
  [CAPSULE_ICON_STRINGS.checkout_allergy]: styles.checkoutIcon,
  [CAPSULE_ICON_STRINGS.checkout_photo]: styles.checkoutIcon,
  [CAPSULE_ICON_STRINGS.paper_icon]: styles.paperIcon,
  [CAPSULE_ICON_STRINGS.system_info]: styles.capsuleLogo,
  [CAPSULE_ICON_STRINGS.close]: styles.capsuleLogo,
  [CAPSULE_ICON_STRINGS.camera]: styles.camera,
  [CAPSULE_ICON_STRINGS.minus]: styles.minusIcon,
  [CAPSULE_ICON_STRINGS.add_icon]: styles.addIcon,
};

/**
 * Just performs a lookup on an icon string and throws an error if it's invalid
 *
 * @param string
 * @returns {*}
 */
// There were TS errors related to iconSrc having type IconString because CAPSULE_ICON_STRINGS should
// be an enum. However there are issues with TS and using enums as indices, as below, so the easiest
// thing to do is just change this to a string, which resolves several TS errors throughout the code.
function getIconSrcFromIconString(iconString: string): string {
  const iconSrc: string | undefined = ICON_STRING_TO_SRC_MAP?.[iconString];

  if (isEmpty(iconSrc)) {
    throw new Error(`iconString is invalid! ${iconString} was provided.`);
  }

  return iconSrc!;
}

export interface CapsuleIconProps extends SVGProps<SVGElement> {
  block?: boolean;
  className?: string;
  iconString: string;
  onClick?: () => void;
  raw?: boolean;
}

const CapsuleIcon = ({ iconString, className, block, ...otherProps }: CapsuleIconProps) => {
  const iconSrc: string = getIconSrcFromIconString(iconString);
  return (
    <InlineSVG
      aria-hidden={true}
      data-testid={CAPSULE_ICON}
      src={iconSrc}
      className={classNames(className, {
        [DEFAULT_ICON_CLASS]: !ICON_TO_CLASS_MAP[iconString],
        [ICON_TO_CLASS_MAP[iconString]]: ICON_TO_CLASS_MAP[iconString],
        [styles.block]: block,
      })}
      {...otherProps}
    />
  );
};

export default CapsuleIcon;
